import {
  Caption1,
  Menu,
  MenuDivider,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import {
  Add12Regular,
  ArrowLeft16Regular,
  Attach16Regular,
  Calendar16Regular,
  ChevronDown12Regular,
  Comment16Regular,
  Delete16Regular,
  Info16Regular,
  Link16Regular,
  LockClosed16Regular,
  DocumentBulletListMultipleRegular,
  MoreHorizontal16Regular,
  People16Regular,
} from "@fluentui/react-icons";
import { Component, lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ConfirmDialog } from "@/components/app-dialog";
import { FuiButton } from "@/components/fui-control";
import { shellGutter } from "@/shell/content-frame";
import { usePageAction } from "@/shell/page-action";
import type { AttachmentLink, DocumentBlock, DocumentKind, DocumentLinkTargetKind } from "@/engine/types";
import { BLOCKED_EDIT_HINT } from "@/features/access/read-only-hint";
import { canCreateOwnTaskNow, usePlanSurface } from "@/features/access/use-plan-access";
import type { DetailTab } from "@/features/tasks/detail-parts";
import { DateInput } from "@/features/tasks/pickers";
import { formatDate } from "@/lib/date-format";
import { useCatalogStore } from "@/store/catalog-store";
import { recordCode } from "@/store/record-ops";
import { newId } from "@/store/tree-ops";
import { useProjectStore } from "@/store/project-store";
import { attachmentUrl, plainTextFromBlocks } from "./doc-blocks";
import { DocTemplateBar } from "./DocTemplateBar";
import { DocTemplateModal, type DocTemplateModalIntent } from "./DocTemplateModal";
import { blocksFromOffer, blocksFromSections, editorTemplateOffers, sectionsFromBlocks } from "./doc-templates";
import { canWorkspace, useEffectiveWorkspaceRole } from "@/features/access/workspace-access";
import { DocumentLinkList } from "./DocumentLinkList";
import { DocKindIcon, DocVisibilityChoices } from "./DocLinkChip";
import { DocLinkFinder, DocRail } from "./DocRail";
import { mentionChipLabel, mentionTargetAlive } from "./doc-links";
import { openMentionTarget } from "./doc-open";
import {
  DOCUMENT_HOST,
  DOCUMENT_KIND_LABEL,
  DOCUMENT_KINDS,
  DOCUMENT_VISIBILITY_LABEL,
  clearDocumentDraft,
  documentCode,
  documentTitle,
  documentWriteBlocked,
  peekDocumentDraft,
  rememberDocumentDraft,
  selectionTitle,
} from "./doc-model";
import { linkedExecutionIds, useDocReader } from "./doc-reader";
import type { DocEditorHandle } from "./DocBlockEditor";
import {
  canDeleteDocument,
  canEditDocument,
  canMakePrivate,
  canPurgeDocument,
  canReadDocument,
  canReadDocumentContent,
  canRestoreDocument,
  orphanNeedsPromotion,
  visibilityCopy,
} from "./doc-visibility";

const DocBlockEditor = lazy(() => import("./DocBlockEditor").then((mod) => ({ default: mod.DocBlockEditor })));

const aliveDocs = new Set<string>();

const useStyles = makeStyles({
  page: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    overflowY: "auto",
  },
  topBar: {
    position: "sticky",
    top: 0,
    zIndex: 5,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalM,
    height: "44px",
    minHeight: "44px",
    paddingLeft: shellGutter,
    paddingRight: shellGutter,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    boxSizing: "border-box",
    flexShrink: 0,
  },
  barLeft: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
  },
  barRight: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    flexShrink: 0,
  },
  sep: {
    color: tokens.colorNeutralStroke1,
    userSelect: "none",
    paddingLeft: "2px",
    paddingRight: "2px",
  },
  meta: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  saveStatus: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    whiteSpace: "nowrap",
    marginRight: tokens.spacingHorizontalXS,
  },
  fail: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: "nowrap",
  },
  kindPill: {
    border: "none",
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground2,
    font: "inherit",
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "3px 8px",
    borderRadius: tokens.borderRadiusMedium,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
  },
  kindStatic: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "3px 6px",
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
  },
  actionPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    height: "28px",
    padding: "0 8px",
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground2,
    font: "inherit",
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    cursor: "pointer",
    whiteSpace: "nowrap",
    transitionProperty: "background-color, color",
    transitionDuration: tokens.durationFaster,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
  },
  actionPillActive: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
  },
  countText: {
    fontVariantNumeric: "tabular-nums",
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
  },
  visibilityPopover: {
    width: "260px",
    padding: tokens.spacingHorizontalM,
  },
  linkPopover: {
    width: "300px",
    padding: tokens.spacingHorizontalS,
  },
  canvas: {
    width: "100%",
    maxWidth: "740px",
    marginLeft: "auto",
    marginRight: "auto",
    paddingTop: "40px",
    paddingBottom: "120px",
    paddingLeft: "28px",
    paddingRight: "28px",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    flexGrow: 1,
  },
  banner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
    padding: "6px 12px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
  },
  title: {
    width: "100%",
    border: "none",
    borderBottom: "1px solid transparent",
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground1,
    font: "inherit",
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightRegular,
    lineHeight: tokens.lineHeightBase500,
    letterSpacing: "-0.02em",
    padding: "4px 0 6px",
    transitionProperty: "border-color",
    transitionDuration: tokens.durationFaster,
    "::placeholder": {
      color: tokens.colorNeutralForeground4,
    },
    ":focus": { outline: "none" },
    ":focus-visible": {
      outline: "none",
      borderBottomColor: tokens.colorStrokeFocus2,
    },
  },
  docMetaRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    marginBottom: tokens.spacingVerticalM,
    minHeight: "28px",
  },
  docMetaItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
  },
  dot: {
    color: tokens.colorNeutralStroke1,
    userSelect: "none",
  },
  outcome: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalS,
  },
  addLinkPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    height: "24px",
    padding: "0 8px",
    borderRadius: tokens.borderRadiusCircular,
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    font: "inherit",
    fontSize: tokens.fontSizeBase200,
    cursor: "pointer",
    ":hover": {
      color: tokens.colorNeutralForeground2,
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
  },
  body: {
    minWidth: 0,
    width: "100%",
    flexGrow: 1,
  },
  retry: {
    border: "none",
    backgroundColor: "transparent",
    color: tokens.colorBrandForeground1,
    font: "inherit",
    fontSize: tokens.fontSizeBase200,
    cursor: "pointer",
    padding: "2px 6px",
    borderRadius: tokens.borderRadiusSmall,
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
});

type SaveLabel = "saved" | "saving" | "error";

export function DocEditor({ docId }: { docId: string }) {
  const styles = useStyles();
  const navigate = useNavigate();
  const { id: projectId = "" } = useParams();
  const project = useProjectStore((s) => s.project);
  const activeId = useProjectStore((s) => s.activeId);
  const schedule = useProjectStore((s) => s.derived.schedule);
  const reader = useDocReader();
  const surface = usePlanSurface();
  const doc = project.documents.find((item) => item.id === docId);
  const titleRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<DocEditorHandle>(null);
  const [saveLabel, setSaveLabel] = useState<SaveLabel>("saved");
  const [uploadError, setUploadError] = useState("");
  const [pasteError, setPasteError] = useState("");
  const [retry, setRetry] = useState(0);
  const [promote, setPromote] = useState(false);
  const [confirmPrivate, setConfirmPrivate] = useState(false);
  const [templateIntent, setTemplateIntent] = useState<DocTemplateModalIntent | null>(null);
  const workspaceRole = useEffectiveWorkspaceRole();
  const canManageTemplates = canWorkspace(workspaceRole, "workspace.settings.update");
  const [confirmTrash, setConfirmTrash] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(false);
  const [drawerTab, setDrawerTab] = useState<DetailTab | null>(null);
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  const [visibilityPopoverOpen, setVisibilityPopoverOpen] = useState(false);
  const [titleDraft, setTitleDraft] = useState<string | null>(null);
  const storeKey = JSON.stringify(doc?.blocks ?? []);
  const [live, setLive] = useState<{ key: string; blocks: DocumentBlock[] } | null>(null);
  const pending = useRef<DocumentBlock[] | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const titleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const titleDraftRef = useRef<string | null>(null);
  const flushRef = useRef<() => void>(() => {});

  usePageAction(null);

  const flush = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    const blocks = pending.current;
    if (!blocks) return;
    pending.current = null;
    setSaveLabel("saving");
    const ok = !documentWriteBlocked() && useProjectStore.getState().saveDocumentBody(docId, blocks);
    if (ok) {
      clearDocumentDraft(docId);
      setSaveLabel("saved");
    } else {
      rememberDocumentDraft(docId, blocks);
      pending.current = blocks;
      setSaveLabel("error");
    }
  }, [docId]);
  flushRef.current = flush;

  const queue = useCallback(
    (blocks: DocumentBlock[]) => {
      pending.current = blocks;
      setSaveLabel("saving");
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => flushRef.current(), 600);
    },
    [],
  );

  useEffect(() => {
    aliveDocs.add(docId);
    return () => {
      aliveDocs.delete(docId);
      const id = docId;
      if (titleTimer.current) clearTimeout(titleTimer.current);
      const title = titleDraftRef.current;
      if (title != null) {
        titleDraftRef.current = null;
        useProjectStore.getState().patchDocument(id, { title });
      }
      queueMicrotask(() => {
        if (aliveDocs.has(id)) return;
        flushRef.current();
        useProjectStore.getState().abandonBlankDocument(id);
      });
    };
  }, [docId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        flushRef.current();
      }
    };
    const onHide = () => {
      if (document.visibilityState === "hidden") flushRef.current();
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("visibilitychange", onHide);
    const onPageHide = () => flushRef.current();
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, []);

  useEffect(() => {
    if (activeId !== projectId) return;
    if (!doc || (!doc.deletedAt && !canReadDocument(doc, reader))) {
      navigate(`/projects/${projectId}/docs?missing=1`, { replace: true });
    }
  }, [activeId, projectId, doc, reader, navigate]);

  const blocks = live && live.key === storeKey ? live.blocks : (peekDocumentDraft(docId) ?? doc?.blocks ?? []);
  const workspaceTemplates = useCatalogStore((s) => s.workspaces[s.activeWorkspaceId]?.docTemplates);
  const linked = doc ? linkedExecutionIds(doc.id, project.documentLinks) : [];
  const canEdit = !!doc && !doc.deletedAt && canEditDocument(doc, reader, linked) && canReadDocumentContent(doc, reader);
  const canDelete = !!doc && !doc.deletedAt && canDeleteDocument(doc, reader, linked);
  const canPrivate = !!doc && !doc.deletedAt && canMakePrivate(doc, reader);
  const canTask = canEdit && surface.editTask && canCreateOwnTaskNow();
  const canIssue = canEdit && surface.editIssue;
  const templateOffers = useMemo(() => editorTemplateOffers(workspaceTemplates ?? []), [workspaceTemplates]);
  const bodyHasText = plainTextFromBlocks(blocks).trim().length > 0;

  const links = useMemo(
    () => (doc ? project.documentLinks.filter((link) => link.documentId === doc.id) : []),
    [doc, project.documentLinks],
  );
  const activeComments = useMemo(
    () => (doc ? doc.comments.filter((c) => !c.deleted).length : 0),
    [doc],
  );
  const catalog = useMemo(
    () => ({
      executionTasks: project.executionTasks,
      tasks: project.tasks.map((task) => ({
        id: task.id,
        name: task.name,
        wbs: schedule.tasks.get(task.id)?.wbs,
        percentComplete: task.percentComplete,
      })),
      records: project.records,
    }),
    [project.executionTasks, project.tasks, project.records, schedule.tasks],
  );

  const mentionLabel = useCallback(
    (kind: string, id: string) => mentionChipLabel(kind, id, catalog, project.documentLinks, doc?.id),
    [catalog, project.documentLinks, doc?.id],
  );
  const mentionAlive = useCallback((kind: string, id: string) => mentionTargetAlive(kind, id, catalog), [catalog]);

  const mentionItems = useCallback((query: string) => {
    const state = useProjectStore.getState();
    const q = query.trim().toLowerCase();
    const items: Array<{ title: string; targetKind: string; targetId: string; onItemClick: () => void }> = [];
    const push = (targetKind: string, targetId: string, title: string, code?: string) => {
      const label = [code, title].filter(Boolean).join(" ");
      if (q && !label.toLowerCase().includes(q)) return;
      items.push({ title: label, targetKind, targetId, onItemClick: () => {} });
    };
    for (const task of state.project.executionTasks) push("executionTask", task.id, task.title || "제목 없음");
    for (const task of state.project.tasks) push("scheduleTask", task.id, task.name || "이름 없는 작업", state.derived.schedule.tasks.get(task.id)?.wbs);
    for (const record of state.project.records) push("record", record.id, record.title || "제목 없음", recordCode(record));
    return items.slice(0, 12);
  }, []);

  const uploadFile = useCallback(
    async (file: File) => {
      const me = useCatalogStore.getState().profile.displayName.trim();
      try {
        const link = await readAttachment(file, me);
        const current = useProjectStore.getState().project.documents.find((item) => item.id === docId);
        if (!current) throw new Error("missing");
        useProjectStore.getState().setDocumentAttachments(docId, [...current.attachments, link]);
        setUploadError("");
        return attachmentUrl(link.id);
      } catch {
        setUploadError("이미지를 첨부하지 못했습니다");
        throw new Error("이미지를 첨부하지 못했습니다");
      }
    },
    [docId],
  );

  const resolveUrl = useCallback(
    async (url: string) => {
      if (!url.startsWith("attachment:")) return url.startsWith("data:") ? "" : url;
      const id = url.slice("attachment:".length);
      const current = useProjectStore.getState().project.documents.find((item) => item.id === docId);
      return current?.attachments.find((item) => item.id === id)?.url ?? "";
    },
    [docId],
  );

  const openLinkedTarget = (kind: DocumentLinkTargetKind, id: string) => {
    openMentionTarget(kind, id);
  };

  const toggleDrawer = (targetTab: DetailTab) => {
    setDrawerTab((current) => (current === targetTab ? null : targetTab));
  };

  if (activeId !== projectId || !doc || (!doc.deletedAt && !canReadDocument(doc, reader))) {
    return (
      <div className={styles.page}>
        <div className={styles.canvas}>
          <Caption1 className={styles.meta}>문서를 여는 중</Caption1>
        </div>
      </div>
    );
  }

  if (orphanNeedsPromotion(doc, reader)) {
    return (
      <div className={styles.page}>
        <header className={styles.topBar}>
          <div className={styles.barLeft}>
            <FuiButton appearance="subtle" icon={<ArrowLeft16Regular />} onClick={() => navigate(`/projects/${projectId}/docs`)}>
              목록
            </FuiButton>
            <span className={styles.sep}>/</span>
            <span className={styles.meta}>{documentCode(doc.seq)}</span>
          </div>
        </header>
        <div className={styles.canvas}>
          <div className={styles.title}>{documentTitle(doc.title)}</div>
          <div className={styles.banner}>
            <Caption1>작성자가 플랜을 떠나 제목만 보입니다. 본문을 열려면 공개로 바꾸세요.</Caption1>
            <FuiButton appearance="primary" size="small" onClick={() => setPromote(true)}>
              공개로 전환
            </FuiButton>
          </div>
        </div>
        <ConfirmDialog
          open={promote}
          title="공개로 바꿀까요?"
          message="본문과 첨부를 이 플랜 멤버가 볼 수 있습니다."
          confirmLabel="공개로 전환"
          onCancel={() => setPromote(false)}
          onConfirm={() => {
            useProjectStore.getState().setDocumentVisibility(doc.id, "plan");
            setPromote(false);
          }}
        />
      </div>
    );
  }

  const readOnly = !canEdit;
  const saveText = saveLabel === "saving" ? "저장 중" : saveLabel === "error" ? "저장하지 못했습니다" : "저장됨";
  const visCopy = visibilityCopy(doc.visibility, DOCUMENT_HOST);

  return (
    <div className={styles.page}>
      <header className={styles.topBar}>
        <div className={styles.barLeft}>
          <FuiButton appearance="subtle" icon={<ArrowLeft16Regular />} onClick={() => navigate(`/projects/${projectId}/docs`)}>
            목록
          </FuiButton>
          <span className={styles.sep} aria-hidden>
            /
          </span>
          <span className={styles.meta}>{documentCode(doc.seq)}</span>
          <KindMenu kind={doc.kind} disabled={readOnly} onChange={(kind) => useProjectStore.getState().patchDocument(doc.id, { kind })} />
        </div>

        <div className={styles.barRight}>
          <span className={saveLabel === "error" ? styles.fail : styles.saveStatus}>{saveText}</span>
          {saveLabel === "error" ? (
            <button type="button" className={styles.retry} onClick={() => flush()}>
              다시 시도
            </button>
          ) : null}

          <Popover
            open={visibilityPopoverOpen}
            onOpenChange={(_, data) => setVisibilityPopoverOpen(data.open)}
            positioning="below-end"
          >
            <Tooltip content={`구분 · ${DOCUMENT_VISIBILITY_LABEL[doc.visibility]}`} relationship="label">
              <PopoverTrigger disableButtonEnhancement>
                <button
                  type="button"
                  className={mergeClasses(styles.actionPill, visibilityPopoverOpen && styles.actionPillActive)}
                  aria-label="구분"
                >
                  {doc.visibility === "private" ? <LockClosed16Regular /> : <People16Regular />}
                  <span>{DOCUMENT_VISIBILITY_LABEL[doc.visibility]}</span>
                </button>
              </PopoverTrigger>
            </Tooltip>
            <PopoverSurface className={styles.visibilityPopover}>
              <DocVisibilityChoices
                value={doc.visibility}
                privateBody={visibilityCopy("private", DOCUMENT_HOST).body}
                planBody={visibilityCopy("plan", DOCUMENT_HOST).body}
                localNote={visCopy.localNote}
                disabled={readOnly}
                privateDisabled={!canPrivate}
                onPlan={() => {
                  useProjectStore.getState().setDocumentVisibility(doc.id, "plan");
                  setVisibilityPopoverOpen(false);
                }}
                onPrivate={() => {
                  if (doc.visibility === "private") return;
                  setVisibilityPopoverOpen(false);
                  setConfirmPrivate(true);
                }}
              />
            </PopoverSurface>
          </Popover>

          <Tooltip content="댓글" relationship="label">
            <button
              type="button"
              className={mergeClasses(styles.actionPill, drawerTab === "comments" && styles.actionPillActive)}
              onClick={() => toggleDrawer("comments")}
              aria-label="댓글"
            >
              <Comment16Regular />
              {activeComments > 0 ? <span className={styles.countText}>{activeComments}</span> : null}
            </button>
          </Tooltip>

          <Tooltip content="첨부 파일" relationship="label">
            <button
              type="button"
              className={mergeClasses(styles.actionPill, drawerTab === "attachments" && styles.actionPillActive)}
              onClick={() => toggleDrawer("attachments")}
              aria-label="첨부 파일"
            >
              <Attach16Regular />
              {doc.attachments.length > 0 ? <span className={styles.countText}>{doc.attachments.length}</span> : null}
            </button>
          </Tooltip>

          <Tooltip content="문서 정보 및 연결" relationship="label">
            <button
              type="button"
              className={mergeClasses(
                styles.actionPill,
                (drawerTab === "details" || drawerTab === "activity") && styles.actionPillActive,
              )}
              onClick={() => toggleDrawer("details")}
              aria-label="문서 정보 및 연결"
            >
              <Info16Regular />
              {links.length > 0 ? <span className={styles.countText}>{links.length}</span> : null}
            </button>
          </Tooltip>

          <Menu positioning="below-end">
            <MenuTrigger disableButtonEnhancement>
              <FuiButton appearance="subtle" icon={<MoreHorizontal16Regular />} aria-label="문서 메뉴" />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem icon={<Link16Regular />} onClick={() => setDrawerTab("details")}>
                  연결 및 문서 속성
                </MenuItem>
                <MenuItem icon={<Comment16Regular />} onClick={() => setDrawerTab("comments")}>
                  댓글 {activeComments > 0 ? `(${activeComments})` : ""}
                </MenuItem>
                <MenuItem icon={<Attach16Regular />} onClick={() => setDrawerTab("attachments")}>
                  첨부 파일 {doc.attachments.length > 0 ? `(${doc.attachments.length})` : ""}
                </MenuItem>
                {!doc.deletedAt && canManageTemplates && bodyHasText ? (
                  <MenuItem
                    icon={<DocumentBulletListMultipleRegular />}
                    onClick={() =>
                      setTemplateIntent({
                        type: "draft",
                        draft: { name: doc.title.trim() || "새 템플릿", kind: doc.kind, sections: sectionsFromBlocks(blocks), blocks },
                      })
                    }
                  >
                    현재 문서를 템플릿으로 저장
                  </MenuItem>
                ) : null}
                {!doc.deletedAt && !readOnly ? (
                  <>
                    <MenuDivider />
                    {doc.visibility === "private" ? (
                      <MenuItem
                        icon={<People16Regular />}
                        onClick={() => useProjectStore.getState().setDocumentVisibility(doc.id, "plan")}
                      >
                        공개로 전환
                      </MenuItem>
                    ) : canPrivate ? (
                      <MenuItem icon={<LockClosed16Regular />} onClick={() => setConfirmPrivate(true)}>
                        비공개로 전환
                      </MenuItem>
                    ) : null}
                    {canDelete ? (
                      <MenuItem icon={<Delete16Regular />} onClick={() => setConfirmTrash(true)}>
                        휴지통으로
                      </MenuItem>
                    ) : null}
                  </>
                ) : null}
                {doc.deletedAt ? (
                  <>
                    <MenuDivider />
                    {canRestoreDocument(doc, reader) ? (
                      <MenuItem onClick={() => useProjectStore.getState().restoreDocument(doc.id)}>복원</MenuItem>
                    ) : null}
                    {canPurgeDocument(doc, reader) ? (
                      <MenuItem icon={<Delete16Regular />} onClick={() => setConfirmPurge(true)}>
                        지우기
                      </MenuItem>
                    ) : null}
                  </>
                ) : null}
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      </header>

      <div className={styles.canvas}>
        {doc.deletedAt ? (
          <div className={styles.banner}>
            <span>휴지통에 있는 문서입니다. 복원하면 다시 편집할 수 있습니다.</span>
            {canRestoreDocument(doc, reader) ? (
              <FuiButton appearance="subtle" size="small" onClick={() => useProjectStore.getState().restoreDocument(doc.id)}>
                복원
              </FuiButton>
            ) : null}
          </div>
        ) : readOnly ? (
          <div className={styles.banner}>
            <span>이 문서를 고칠 수 없습니다</span>
          </div>
        ) : null}

        {pasteError ? <Caption1 className={styles.fail}>{pasteError}</Caption1> : null}
        {uploadError ? <Caption1 className={styles.fail}>{uploadError}</Caption1> : null}

        <input
          ref={titleRef}
          className={styles.title}
          aria-label="제목"
          placeholder="제목 없음"
          value={titleDraft ?? doc.title}
          readOnly={readOnly}
          onChange={(event) => {
            const next = event.target.value;
            titleDraftRef.current = next;
            setTitleDraft(next);
            if (titleTimer.current) clearTimeout(titleTimer.current);
            titleTimer.current = setTimeout(() => {
              titleDraftRef.current = null;
              useProjectStore.getState().patchDocument(doc.id, { title: next });
              setTitleDraft(null);
            }, 600);
          }}
          onBlur={(event) => {
            if (titleTimer.current) clearTimeout(titleTimer.current);
            titleDraftRef.current = null;
            useProjectStore.getState().patchDocument(doc.id, { title: event.target.value });
            setTitleDraft(null);
          }}
          onKeyDown={(event) => {
            if (event.key !== "Enter" && event.key !== "ArrowDown") return;
            event.preventDefault();
            editorRef.current?.focusStart();
          }}
        />

        <div className={styles.docMetaRow}>
          <span className={styles.docMetaItem}>작성자 {doc.authorId || "—"}</span>
          <span className={styles.dot} aria-hidden>
            ·
          </span>
          <span className={styles.docMetaItem}>수정 {formatDate(doc.updatedAt, "dateTime")}</span>

          {doc.kind === "meeting" ? (
            <>
              <span className={styles.dot} aria-hidden>
                ·
              </span>
              <span className={styles.docMetaItem}>
                <Calendar16Regular />
                <span>회의 날짜</span>
                <DateInput
                  value={doc.heldAt?.slice(0, 10)}
                  disabled={readOnly}
                  ariaLabel="회의 날짜"
                  onChange={(value) => useProjectStore.getState().patchDocument(doc.id, { heldAt: value })}
                />
              </span>
            </>
          ) : null}

        </div>

        {links.length > 0 || !readOnly ? (
          <div className={styles.outcome}>
            <DocumentLinkList
              links={links}
              catalog={catalog}
              onOpen={(chip) => openLinkedTarget(chip.targetKind, chip.targetId)}
              onUnlink={readOnly ? undefined : (linkId) => useProjectStore.getState().unlinkDocument(doc.id, linkId)}
            />
            {!readOnly ? (
              <Popover open={linkPopoverOpen} onOpenChange={(_, data) => setLinkPopoverOpen(data.open)} positioning="below-start">
                <PopoverTrigger disableButtonEnhancement>
                  <button type="button" className={styles.addLinkPill} aria-label="연결 추가">
                    <Add12Regular />
                    <span>{links.length ? "연결" : "작업·이슈 연결"}</span>
                  </button>
                </PopoverTrigger>
                <PopoverSurface className={styles.linkPopover}>
                  <DocLinkFinder docId={doc.id} onDone={() => setLinkPopoverOpen(false)} />
                </PopoverSurface>
              </Popover>
            ) : null}
          </div>
        ) : null}

        <div className={styles.body}>
          {canEdit && !bodyHasText ? (
            <DocTemplateBar
              offers={templateOffers}
              onApply={(next) => {
                setLive({ key: storeKey, blocks: next });
                queue(next);
              }}
              onOpenModal={setTemplateIntent}
            />
          ) : null}
          <DocChunkBoundary resetKey={retry} onRetry={() => setRetry((value) => value + 1)}>
            <Suspense fallback={<Caption1 className={styles.meta}>문서를 여는 중</Caption1>}>
              <DocBlockEditor
                key={`${doc.id}:${retry}`}
                ref={editorRef}
                docId={doc.id}
                blocks={blocks}
                editable={canEdit && canReadDocumentContent(doc, reader)}
                mentionLabel={mentionLabel}
                mentionAlive={mentionAlive}
                onOpenMention={openMentionTarget}
                mentionItems={mentionItems}
                resolveUrl={resolveUrl}
                uploadFile={uploadFile}
                onBlocks={(next) => {
                  setLive({ key: storeKey, blocks: next });
                  queue(next);
                }}
                onPasteRejected={setPasteError}
                onFocusTitle={() => titleRef.current?.focus()}
                onCreateWork={(kind, title, blockId) => {
                  const text = selectionTitle(title);
                  if (!text) return;
                  useProjectStore.getState().createWorkFromDocument({ documentId: doc.id, kind, title: text, blockId });
                }}
                onPlanUndo={() => useProjectStore.getState().undo()}
                canCreateTask={canTask}
                canCreateIssue={canIssue}
                taskHint={canTask ? undefined : canEdit ? BLOCKED_EDIT_HINT : "이 문서를 고칠 수 없습니다"}
                issueHint={canIssue ? undefined : canEdit ? BLOCKED_EDIT_HINT : "이 문서를 고칠 수 없습니다"}
              />
            </Suspense>
          </DocChunkBoundary>
        </div>
      </div>

      <DocRail
        doc={doc}
        reader={reader}
        canEdit={canEdit}
        uploadError={uploadError}
        open={drawerTab !== null}
        tab={drawerTab ?? "details"}
        onTabChange={(next) => setDrawerTab(next)}
        onClose={() => setDrawerTab(null)}
      />

      <ConfirmDialog
        open={confirmPrivate}
        title="비공개로 바꿀까요?"
        message="다른 멤버의 목록과 연결 칩에서 이 문서가 사라집니다. 작업과 이슈는 그대로입니다."
        confirmLabel="비공개로 전환"
        onCancel={() => setConfirmPrivate(false)}
        onConfirm={() => {
          useProjectStore.getState().setDocumentVisibility(doc.id, "private");
          setConfirmPrivate(false);
        }}
      />

      <ConfirmDialog
        open={confirmTrash}
        title="문서를 휴지통으로 옮길까요"
        message="이 문서를 휴지통으로 옮깁니다. 연결된 작업과 이슈는 남고, 그쪽 목록에서는 이 문서가 잠시 숨습니다."
        confirmLabel="휴지통으로"
        onCancel={() => setConfirmTrash(false)}
        onConfirm={() => {
          useProjectStore.getState().trashDocument(doc.id);
          setConfirmTrash(false);
          navigate(`/projects/${projectId}/docs`);
        }}
      />

      <ConfirmDialog
        open={confirmPurge}
        title="문서를 지울까요"
        message="이 문서를 지웁니다. 이 문서의 연결도 함께 사라집니다."
        confirmLabel="지우기"
        onCancel={() => setConfirmPurge(false)}
        onConfirm={() => {
          useProjectStore.getState().purgeDocument(doc.id);
          setConfirmPurge(false);
          navigate(`/projects/${projectId}/docs?tab=trash`);
        }}
      />
      {templateIntent ? (
        <DocTemplateModal
          open
          intent={templateIntent}
          onClose={() => setTemplateIntent(null)}
          applyLabel="이 템플릿 적용"
          {...(canEdit && !bodyHasText
            ? {
                onApply: (offer) => {
                  const next = blocksFromOffer(offer);
                  setLive({ key: storeKey, blocks: next });
                  queue(next);
                  setTemplateIntent(null);
                },
              }
            : {})}
        />
      ) : null}
    </div>
  );
}

function KindMenu({ kind, disabled, onChange }: { kind: DocumentKind; disabled?: boolean; onChange: (kind: DocumentKind) => void }) {
  const styles = useStyles();
  if (disabled) {
    return (
      <span className={styles.kindStatic}>
        <DocKindIcon kind={kind} />
        <span>{DOCUMENT_KIND_LABEL[kind]}</span>
      </span>
    );
  }
  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <button type="button" className={styles.kindPill}>
          <DocKindIcon kind={kind} />
          <span>{DOCUMENT_KIND_LABEL[kind]}</span>
          <ChevronDown12Regular />
        </button>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {DOCUMENT_KINDS.map((item) => (
            <MenuItem key={item} icon={<DocKindIcon kind={item} />} onClick={() => onChange(item)}>
              {DOCUMENT_KIND_LABEL[item]}
            </MenuItem>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

function DocChunkBoundary({ children, resetKey, onRetry }: { children: ReactNode; resetKey: number; onRetry: () => void }) {
  const styles = useStyles();
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [resetKey]);
  if (failed) {
    return (
      <button type="button" className={styles.retry} onClick={onRetry}>
        다시 시도
      </button>
    );
  }
  return (
    <Boundary onError={() => setFailed(true)} resetKey={resetKey}>
      {children}
    </Boundary>
  );
}

class Boundary extends Component<{ children: ReactNode; onError: () => void; resetKey: number }, { error: boolean }> {
  override state = { error: false };
  static getDerivedStateFromError() {
    return { error: true };
  }
  override componentDidUpdate(prev: { resetKey: number }) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: false });
  }
  override componentDidCatch() {
    this.props.onError();
  }
  override render() {
    if (this.state.error) return null;
    return this.props.children;
  }
}

function readAttachment(file: File, addedBy: string): Promise<AttachmentLink> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve({
        id: newId("att"),
        title: file.name,
        url: String(reader.result ?? ""),
        kind: file.type.startsWith("image/") ? "image" : "file",
        size: file.size,
        ...(file.type ? { mimeType: file.type } : {}),
        addedAt: new Date().toISOString(),
        ...(addedBy ? { addedBy } : {}),
      });
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
